/* FinancialSyncAudit — sub-component of FinancialTraceability
 * ────────────────────────────────────────────────────────────
 * Renders:
 *   1. "🔁 Reset Balance" button (super_admin only) with multi-step
 *      confirmation dialog (preview → type-to-confirm → execute).
 *   2. Snapshots list with one-click rollback (super_admin only).
 *   3. Sync audit log (read-only).
 *
 * Wired into FinancialTraceability.jsx as the "sync_audit" tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtDT = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('ar-DZ', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(v); }
};
const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ════════════════════════════════════════════════════════════════════════
   DiagnosticsButtons — open / download the system diagnostics report
   ════════════════════════════════════════════════════════════════════════ */
const DiagnosticsButtons = () => {
  const [busy, setBusy] = useState(false);

  const openHtml = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/system/diagnostics/full-report?format=html`,
        { headers: authHeader(), responseType: 'text' },
      );
      const w = window.open('', '_blank', 'noopener,noreferrer');
      w.document.open();
      w.document.write(r.data);
      w.document.close();
    } catch (e) {
      toast.error('فشل التحميل: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const download = async (format, mime, ext) => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/system/diagnostics/full-report?format=${format}`,
        { headers: authHeader(), responseType: 'text' },
      );
      const blob = new Blob([typeof r.data === 'string' ? r.data : JSON.stringify(r.data, null, 2)], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sanhaja-diagnostics-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`✅ تم تنزيل تقرير ${format.toUpperCase()}`);
    } catch (e) {
      toast.error('فشل التنزيل: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex gap-2 flex-wrap">
      <Button
        variant="outline"
        className="border-blue-400 text-blue-700 hover:bg-blue-50"
        onClick={openHtml}
        disabled={busy}
        data-testid="open-diagnostics-html"
        title="افتح التقرير الكامل في نافذة جديدة"
      >
        🩺 تقرير التشخيص
      </Button>
      <Button
        variant="outline" size="sm"
        onClick={() => download('json', 'application/json', 'json')}
        disabled={busy} data-testid="download-diagnostics-json"
      >⬇ JSON</Button>
      <Button
        variant="outline" size="sm"
        onClick={() => download('md', 'text/markdown', 'md')}
        disabled={busy} data-testid="download-diagnostics-md"
      >⬇ MD</Button>
      <Button
        variant="outline" size="sm"
        onClick={() => download('txt', 'text/plain', 'txt')}
        disabled={busy} data-testid="download-diagnostics-txt"
      >⬇ TXT</Button>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   ResetBalanceDialog — multi-step strong confirmation
   ════════════════════════════════════════════════════════════════════════ */
export const ResetBalanceDialog = ({ open, onClose, onDone }) => {
  const [step, setStep] = useState('preview'); // preview | confirm | working | result
  const [previewData, setPreviewData] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState(null);

  // Load preview when dialog opens
  useEffect(() => {
    if (!open) return;
    setStep('preview');
    setConfirmText('');
    setResult(null);
    setPreviewData(null);
    axios.get(`${API}/financial/sync/preview-reset`, { headers: authHeader() })
      .then((r) => setPreviewData(r.data))
      .catch((e) => toast.error('فشل تحميل المعاينة: '
        + (e?.response?.data?.detail || e.message)));
  }, [open]);

  const execute = async () => {
    setStep('working');
    try {
      const r = await axios.post(
        `${API}/financial/sync/reset-to-current-balance`, {},
        { headers: authHeader() },
      );
      setResult(r.data);
      setStep('result');
      onDone && onDone(r.data);
    } catch (e) {
      toast.error('فشل التنفيذ: ' + (e?.response?.data?.detail || e.message));
      setStep('confirm');
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            ⚠️ إعادة ضبط الرصيد على المرجع الفعلي
          </DialogTitle>
          <DialogDescription>
            عملية حساسة — تحذف الوصولات المُستوردة عبر المزامنة وتُنشئ رصيداً
            افتتاحياً يطابق صيغة النظام القديم. يتم أخذ Snapshot تلقائياً
            قبل التنفيذ للسماح بالتراجع.
          </DialogDescription>
        </DialogHeader>

        {/* ─── STEP 1: PREVIEW ─────────────────────────────────────── */}
        {step === 'preview' && (
          previewData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3">
                  <div className="text-xs text-red-700">سيُحذف</div>
                  <div className="font-bold text-2xl text-red-800" data-testid="rst-to-delete-count">
                    {previewData.to_delete.count}
                  </div>
                  <div className="text-xs text-red-700">
                    وصلاً ({fmtMoney(previewData.to_delete.sum)} دج)
                  </div>
                </div>
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-3">
                  <div className="text-xs text-green-700">يُحافَظ عليه</div>
                  <div className="font-bold text-2xl text-green-800" data-testid="rst-preserved-count">
                    {previewData.manual_preserved.count}
                  </div>
                  <div className="text-xs text-green-700">
                    وصل يدوي ({fmtMoney(previewData.manual_preserved.sum)} دج)
                  </div>
                </div>
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3">
                  <div className="text-xs text-amber-700">الرصيد المتوقع بعد التنفيذ</div>
                  <div className="font-bold text-2xl text-amber-800" data-testid="rst-expected-total">
                    {fmtMoney(previewData.expected_transferable_after_reset)} دج
                  </div>
                </div>
              </div>

              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm" data-testid="rst-agencies-table">
                  <thead className="bg-amber-50">
                    <tr>
                      <th className="p-2 text-start">الوكالة</th>
                      <th className="p-2 text-end">الإيرادات</th>
                      <th className="p-2 text-end">المُحوَّل</th>
                      <th className="p-2 text-end">المصاريف</th>
                      <th className="p-2 text-end">الرصيد المتوقع</th>
                      <th className="p-2 text-center">سيُنشأ وصل افتتاحي؟</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData.agencies || []).map((a) => (
                      <tr key={a.agency_id} className="border-t hover:bg-gray-50"
                          data-testid={`rst-agency-${a.agency_id}`}>
                        <td className="p-2">{a.agency_name || a.agency_id}</td>
                        <td className="p-2 text-end">{fmtMoney(a.revenue)}</td>
                        <td className="p-2 text-end">{fmtMoney(a.transferred)}</td>
                        <td className="p-2 text-end">{fmtMoney(a.expenses)}</td>
                        <td className="p-2 text-end font-bold text-amber-700">{fmtMoney(a.balance)}</td>
                        <td className="p-2 text-center">
                          {a.will_create_opening_entry ? '✅' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-900">
                ✅ <b>Idempotent</b> — آمن للتشغيل مرات متعددة، لن يسبب تضخماً.<br />
                ✅ <b>Rollback</b> — يُحفظ Snapshot تلقائياً ويمكنك التراجع لاحقاً.<br />
                ✅ <b>Audit Log</b> — كل عملية تُسجَّل في سجل المزامنة.
              </div>
            </div>
          ) : (
            <div className="p-10 text-center text-gray-500">جاري تحميل المعاينة...</div>
          )
        )}

        {/* ─── STEP 2: TYPE-TO-CONFIRM ──────────────────────────────── */}
        {step === 'confirm' && (
          <div className="space-y-4">
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4">
              <div className="font-bold text-red-800 mb-2">⛔ تأكيد نهائي</div>
              <p className="text-sm text-red-700">
                ستحذف <b>{previewData?.to_delete.count}</b> وصلاً (
                {fmtMoney(previewData?.to_delete.sum)} دج) وتُنشئ
                <b> {(previewData?.agencies || []).filter(a => a.will_create_opening_entry).length} </b>
                وصلاً افتتاحياً.
              </p>
              <p className="text-sm text-red-700 mt-2">
                للمتابعة، اكتب كلمة <b className="text-red-900">RESET</b> في المربع أدناه:
              </p>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-3 border-red-300 focus:border-red-500"
                placeholder="اكتب RESET..."
                data-testid="rst-confirm-input"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* ─── STEP 3: WORKING ──────────────────────────────────────── */}
        {step === 'working' && (
          <div className="p-12 text-center">
            <div className="animate-spin text-5xl mb-3">⏳</div>
            <div className="font-bold text-amber-700">جارٍ التنفيذ...</div>
            <div className="text-xs text-gray-500 mt-2">لا تُغلق هذه النافذة</div>
          </div>
        )}

        {/* ─── STEP 4: RESULT ───────────────────────────────────────── */}
        {step === 'result' && result && (
          <div className="space-y-4">
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
              <div className="font-bold text-green-800 text-lg mb-2">✅ تم التنفيذ بنجاح</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>وصولات محذوفة: <b>{result.deleted_old_sync_entries}</b></div>
                <div>وصولات افتتاحية جديدة: <b>{result.opening_balance_entries_created}</b></div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-xs">
              <div className="font-bold mb-1">🔖 Snapshot ID للتراجع:</div>
              <code className="font-mono text-xs select-all break-all" data-testid="rst-snapshot-id">
                {result.snapshot_id}
              </code>
              <div className="mt-2 text-gray-600">
                يمكنك التراجع من تبويب «سجل المزامنة» في أي وقت.
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="rst-cancel">إلغاء</Button>
              <Button
                onClick={() => setStep('confirm')}
                disabled={!previewData}
                variant="destructive"
                data-testid="rst-proceed-to-confirm"
              >
                ⚠️ متابعة إلى التأكيد
              </Button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('preview')}>← رجوع</Button>
              <Button
                onClick={execute}
                disabled={confirmText.trim() !== 'RESET'}
                variant="destructive"
                data-testid="rst-execute"
              >
                🔁 تنفيذ إعادة الضبط الآن
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={onClose} className="bg-amber-600 hover:bg-amber-700 text-white">
              إغلاق
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   FinancialSyncAudit Tab Content
   ════════════════════════════════════════════════════════════════════════ */
export const FinancialSyncAudit = ({ currentUser }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/financial/sync/snapshots?limit=50`, { headers: authHeader() }),
        axios.get(`${API}/financial/sync/audit?limit=200`, { headers: authHeader() }),
      ]);
      setSnapshots(s.data.items || []);
      setAudit(a.data.items || []);
    } catch (e) {
      toast.error('فشل تحميل سجل المزامنة: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doRollback = async (snapshotId) => {
    const confirm = window.prompt(
      `للتأكيد، اكتب ROLLBACK لاستعادة ${snapshotId.slice(0, 8)}...`,
    );
    if (confirm?.trim() !== 'ROLLBACK') {
      if (confirm !== null) toast.error('تأكيد غير صحيح');
      return;
    }
    setRollingBack(snapshotId);
    try {
      const r = await axios.post(
        `${API}/financial/sync/rollback/${snapshotId}`, {},
        { headers: authHeader() },
      );
      toast.success(
        `✅ تم الاستعادة — استرجع ${r.data.restored_entries} وصلاً + ${r.data.restored_reconciliations} ربط`,
      );
      load();
    } catch (e) {
      toast.error('فشل التراجع: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRollingBack(null);
    }
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const actionMeta = {
    'sync.reset_to_current_balance': { label: 'إعادة ضبط', cls: 'bg-red-100 text-red-800 border-red-300' },
    'sync.rollback':                  { label: 'تراجع',     cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    'sync.from_payments':             { label: 'مزامنة دفعات',     cls: 'bg-blue-100 text-blue-800 border-blue-300' },
    'sync.from_operation_payments':   { label: 'مزامنة عمليات',    cls: 'bg-blue-100 text-blue-800 border-blue-300' },
    'sync.reconcile_balance':         { label: 'مطابقة',           cls: 'bg-purple-100 text-purple-800 border-purple-300' },
    'sync.rebuild_from_balance_formula': { label: 'إعادة بناء',     cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  };

  return (
    <div className="space-y-4" data-testid="financial-sync-audit">
      {/* Action bar */}
      <Card>
        <CardContent className="pt-4 flex justify-between items-center flex-wrap gap-3">
          <div>
            <div className="font-bold text-lg">🔁 إعادة ضبط الرصيد على المرجع الفعلي</div>
            <div className="text-xs text-gray-600">
              يحذف الوصولات المُستوردة عبر المزامنة وينشئ رصيداً افتتاحياً مطابقاً للنظام القديم.
              <b> آمن — يأخذ Snapshot قبل التنفيذ.</b>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isSuperAdmin && (
              <DiagnosticsButtons />
            )}
            {isSuperAdmin ? (
              <Button
                variant="destructive"
                onClick={() => setResetOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="open-reset-dialog"
              >
                ⚠️ إعادة ضبط الرصيد
              </Button>
            ) : (
              <Badge variant="outline">للمدير العام فقط</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Snapshots */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex justify-between items-center">
            <span>📸 Snapshots المتاحة للتراجع</span>
            <Badge variant="outline">{snapshots.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="p-6 text-center text-gray-500">جاري التحميل...</div>
          ) : snapshots.length === 0 ? (
            <div className="p-8 text-center text-gray-400">لا توجد عمليات إعادة ضبط بعد</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="snapshots-table">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="p-2 text-start">التاريخ</th>
                    <th className="p-2 text-start">بواسطة</th>
                    <th className="p-2 text-end">وصولات محذوفة</th>
                    <th className="p-2 text-end">مجموعها</th>
                    <th className="p-2 text-end">إجمالي بعد الضبط</th>
                    <th className="p-2 text-start">الحالة</th>
                    <th className="p-2 text-start">⚙️</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((s) => (
                    <tr key={s.id} className="border-t hover:bg-amber-50/40"
                        data-testid={`snapshot-row-${s.id}`}>
                      <td className="p-2 text-xs whitespace-nowrap">{fmtDT(s.created_at)}</td>
                      <td className="p-2 text-xs">{s.created_by_name || '—'}</td>
                      <td className="p-2 text-end">{s.entries_count}</td>
                      <td className="p-2 text-end">{fmtMoney(s.entries_sum)}</td>
                      <td className="p-2 text-end font-bold text-amber-700">
                        {fmtMoney(s.post_reset_total || 0)}
                      </td>
                      <td className="p-2">
                        {s.restored_at ? (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                            🔁 مُسترجَع — {fmtDT(s.restored_at)}
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 border border-green-300">✅ نشط</Badge>
                        )}
                      </td>
                      <td className="p-2">
                        {isSuperAdmin && !s.restored_at && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs px-2 py-1 text-amber-700 border-amber-300"
                            disabled={rollingBack === s.id}
                            onClick={() => doRollback(s.id)}
                            data-testid={`rollback-${s.id}`}
                          >
                            {rollingBack === s.id ? '⏳' : '↩️ تراجع'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex justify-between items-center">
            <span>🔍 سجل أحداث المزامنة</span>
            <Badge variant="outline">آخر {audit.length} حدث</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <div className="p-8 text-center text-gray-400">لا توجد أحداث</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sync-audit-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-start">التاريخ</th>
                    <th className="p-2 text-start">الإجراء</th>
                    <th className="p-2 text-start">بواسطة</th>
                    <th className="p-2 text-start">الدور</th>
                    <th className="p-2 text-start">التفاصيل</th>
                    <th className="p-2 text-start">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => {
                    const m = actionMeta[a.action] || { label: a.action, cls: 'bg-gray-100' };
                    const extra = a.extra || {};
                    const summary = extra.deleted != null
                      ? `حذف ${extra.deleted} • أنشأ ${(extra.agencies || []).length}`
                      : extra.restored_entries != null
                        ? `استرجع ${extra.restored_entries}`
                        : extra.created != null
                          ? `أنشأ ${extra.created} • تخطى ${extra.skipped || 0}`
                          : JSON.stringify(extra).slice(0, 80);
                    return (
                      <tr key={a.id} className="border-t hover:bg-gray-50"
                          data-testid={`sync-audit-row-${a.id}`}>
                        <td className="p-2 text-xs whitespace-nowrap">{fmtDT(a.at)}</td>
                        <td className="p-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${m.cls}`}>
                            {m.label}
                          </span>
                        </td>
                        <td className="p-2 text-xs">{a.actor_name || '—'}</td>
                        <td className="p-2 text-xs">{a.actor_role}</td>
                        <td className="p-2 text-xs">{summary}</td>
                        <td className="p-2 text-xs font-mono">{a.ip || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ResetBalanceDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onDone={() => { load(); }}
      />
    </div>
  );
};
